// Hierarchy.jsx
import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import swal from "sweetalert";
import commoncss from "../../css/common.module.css";
import libcss from "../../css/library.module.css";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  getTemplateDetailsApiHandler,
  saveGroupingApiHandler,
  deleteGroupingApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";

import { ReactComponent as Plus } from "../../../myfin_v2/assets/icons/plusprimary.svg";
import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/editPri.svg";
import { ReactComponent as FormulaIcon } from "../../../myfin_v2/assets/icons/formula.svg";
import { ReactComponent as CloseIcon } from "../../../myfin_v2/assets/icons/close.svg";
import closeSideModel from "../../assets/icons/closeSideModel.svg";
import Arrow from "../../assets/icons/Arrow.png";
import MenuIcon from "../../assets/icons/Menu.png";
import AddItemModal from "../../components/templateLibrary/AddItemModal";
import RestoreDeletedItemModal from "../../components/templateLibrary/RestoreDeletedItemModal";
import ShowEditItemModal from "../../components/templateLibrary/ShowEditItemModal";

/*
  CSS (add to library.module.css):
  .draggingItem {
    box-shadow: 0 8px 20px rgba(0,0,0,0.18);
    background: #fff;
    z-index: 9999;
    transform: none !important; // allow react-beautiful-dnd transform to win
  }
*/

const Hierarchy = ({ tempId, showBin, setShowBin, isReadOnly = false }) => {
  const [libraryName] = useState("ST Library");
  const [templateData, setTemplateData] = useState(null);
  const [groupingData, setGroupingData] = useState(null);
  const [deletedGroupingData, setDeletedGroupingData] = useState(null);

  const [addItemModal, setAddItemModal] = useState(false);
  const [addItemLevel, setAddItemLevel] = useState(null);
  const [addItemCat, setAddItemCat] = useState(null);
  const [selCat, setSelCat] = useState(null);

  const [lvl2Data, setlvl2Data] = useState(null);

  // selection: both index and stable id
  const [l1idx, setL1idx] = useState(0);
  const [l1id, setL1id] = useState(null);

  const [l2idx, setL2idx] = useState(null);
  const [l2id, setL2id] = useState(null);

  const [l3idx, setL3idx] = useState(0);

  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editItemLevel, setEditItemLevel] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editContext, setEditContext] = useState(null);

  const [draggingLevel2, setDraggingLevel2] = useState(false);
  const [draggingLevel3, setDraggingLevel3] = useState(false);

  // ---------------- helpers ----------------
  const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

  const applyParentNature = (item, parentNature) => {
    if (!item) return item;
    if (item.lineType === "line item")
      item.nature = item.nature || parentNature || "credit";
    if (Array.isArray(item.subgroup) && item.subgroup.length > 0) {
      item.subgroup = item.subgroup.map((c) =>
        applyParentNature(c, item.nature)
      );
    }
    return item;
  };

  const cleanItem = (item) => {
    if (!item) return item;
    if (item.lineType === "line item") {
      const { formula, ...rest } = item;
      if (Array.isArray(rest.subgroup) && rest.subgroup.length > 0)
        rest.subgroup = rest.subgroup.map(cleanItem);
      return rest;
    } else if (item.lineType === "formula") {
      const { nature, subgroup, ...rest } = item;
      if (Array.isArray(item.formula) && item.formula.length > 0)
        rest.formula = item.formula.map((f) => ({ ...f }));
      return rest;
    }
    return item;
  };

  const prepareAndSave = (newGrouping) => {
    const groupingWithNature = newGrouping.map((item) =>
      applyParentNature(deepClone(item), item.nature)
    );
    const cleanedGrouping = groupingWithNature.map(cleanItem);
    const payload = { grouping: cleanedGrouping };

    return saveGroupingApiHandler({ groupingTemplateID: tempId }, payload)
      .then((response) => {
        if (response?.data?.success) {
          // refresh authoritative data after a successful save
          getTemplateDetails();
          return true;
        }
        return false;
      })
      .catch(() => false);
  };

  // ---------------- fetch ----------------
  useEffect(() => {
    getTemplateDetails();
    // eslint-disable-next-line
  }, [tempId, showBin]);

  function getTemplateDetails() {
    setTemplateData(null);
    setGroupingData(null);
    setDeletedGroupingData(null);
    getTemplateDetailsApiHandler({ groupingTemplateID: tempId })
      .then((response) => {
        if (response?.data?.success === true) {
          const tpl = response.data.groupingTemplate;
          setTemplateData(tpl);
          setGroupingData(tpl.grouping || []);
          setDeletedGroupingData(tpl.deletedGrouping || []);
        }
      })
      .catch(() => {});
  }

  // sync lvl2 when groupingData or l1id changes
  useEffect(() => {
    if (!groupingData) {
      setlvl2Data(null);
      return;
    }
    const l1 = groupingData.find((g) => g._id === l1id);
    const sub = l1?.subgroup ?? [];
    setlvl2Data(sub);

    if (sub.length === 0) {
      setL2idx(null);
      setL2id(null);
    } else {
      if (l2idx === null) {
        setL2idx(0);
        setL2id(sub[0]._id ?? null);
      } else if (l2idx >= sub.length) {
        const newIdx = Math.max(0, sub.length - 1);
        setL2idx(newIdx);
        setL2id(sub[newIdx]?._id ?? null);
      } else {
        setL2id(sub[l2idx]?._id ?? null);
      }
    }
    // eslint-disable-next-line
  }, [groupingData, l1id]);

  // ---------------- AddItemHandler (kept) ----------------
  const AddItemHandler = (itemObjs) => {
    const deepCloneLocal = (obj) => JSON.parse(JSON.stringify(obj));
    const applyParentNatureLocal = (item, parentNature) => {
      if (item.lineType === "line item")
        item.nature = item.nature || parentNature || "credit";
      if (Array.isArray(item.subgroup) && item.subgroup.length > 0) {
        item.subgroup = item.subgroup.map((child) =>
          applyParentNatureLocal(child, item.nature)
        );
      }
      return item;
    };
    const cleanItemLocal = (item) => {
      if (item.lineType === "line item") {
        const { formula, ...rest } = item;
        if (Array.isArray(rest.subgroup) && rest.subgroup.length > 0)
          rest.subgroup = rest.subgroup.map(cleanItemLocal);
        return rest;
      } else if (item.lineType === "formula") {
        const { nature, subgroup, ...rest } = item;
        if (Array.isArray(item.formula) && item.formula.length > 0)
          rest.formula = item.formula.map((f) => ({ ...f }));
        return rest;
      } else return item;
    };

    if (itemObjs.level === 1 && !itemObjs.subgroup) itemObjs.subgroup = [];
    if (itemObjs.level === 1 && !itemObjs.nature) itemObjs.nature = "credit";

    const processedItem = applyParentNatureLocal(
      deepCloneLocal(itemObjs),
      itemObjs.nature
    );
    const updatedGroupingData = groupingData
      ? [...groupingData, processedItem]
      : [processedItem];
    const groupingWithNature = updatedGroupingData.map((item) =>
      applyParentNatureLocal(deepCloneLocal(item), item.nature)
    );
    const cleanedGrouping = groupingWithNature.map(cleanItemLocal);
    const payload = { grouping: cleanedGrouping };

    saveGroupingApiHandler({ groupingTemplateID: tempId }, payload)
      .then((response) => {
        if (response?.data?.success) {
          getTemplateDetails();
          setAddItemModal(false);
        }
      })
      .catch(() => {});
  };

  // ---------------- addToSubgroup (kept) ----------------
  const addToSubgroup = (path, newObj, category) => {
    const deepCloneLocal = (obj) => JSON.parse(JSON.stringify(obj));
    const applyParentNatureLocal = (item, parentNature) => {
      if (item.lineType === "line item")
        item.nature = item.nature || parentNature || "credit";
      if (Array.isArray(item.subgroup) && item.subgroup.length > 0) {
        item.subgroup = item.subgroup.map((c) =>
          applyParentNatureLocal(c, item.nature)
        );
      }
      return item;
    };
    const cleanItemLocal = (item) => {
      if (item.lineType === "line item") {
        const { formula, ...rest } = item;
        if (Array.isArray(rest.subgroup) && rest.subgroup.length > 0)
          rest.subgroup = rest.subgroup.map(cleanItemLocal);
        return rest;
      } else if (item.lineType === "formula") {
        const { nature, subgroup, ...rest } = item;
        if (Array.isArray(item.formula) && item.formula.length > 0)
          rest.formula = item.formula.map((f) => ({ ...f }));
        return rest;
      } else return item;
    };

    const newData = deepCloneLocal(groupingData || []);
    let effectivePath = Array.isArray(path) ? [...path] : [];

    if (typeof category !== "undefined" && category !== null) {
      const level1Index = newData.findIndex((it) => it?.category === category);
      if (level1Index === -1) {
        console.warn(
          `addToSubgroup: no top-level category "${category}" found — skipping add`
        );
        return;
      }
      if (effectivePath.length === 0) effectivePath = [level1Index];
      else {
        const first = effectivePath[0];
        const firstIsExplicit =
          typeof first === "string" ||
          typeof first === "number" ||
          (typeof first === "object" && first !== null);
        if (!firstIsExplicit) effectivePath[0] = level1Index;
      }
    }

    const resolveIndexInArray = (arr, key) => {
      if (!Array.isArray(arr)) return -1;
      if (typeof key === "number") return key;
      if (typeof key === "string") {
        const byId = arr.findIndex((it) => it && it._id === key);
        if (byId !== -1) return byId;
        return arr.findIndex((it) => it && it.id === key);
      }
      if (typeof key === "object" && key !== null) {
        const lookup = key._id ?? key.id;
        if (typeof lookup === "string" || typeof lookup === "number")
          return arr.findIndex(
            (it) => it && (it._id === lookup || it.id === lookup)
          );
      }
      return -1;
    };

    let targetIsTopLevel = effectivePath.length === 0;
    let targetObj = null;
    let currentArray = newData;

    if (!targetIsTopLevel) {
      for (let i = 0; i < effectivePath.length; i++) {
        const key = effectivePath[i];
        const idx = resolveIndexInArray(currentArray, key);
        if (idx < 0 || idx >= currentArray.length) {
          console.warn(
            `addToSubgroup: path element "${JSON.stringify(
              key
            )}" not found (resolved idx ${idx}) at depth ${i}`
          );
          return;
        }
        const currentObj = currentArray[idx];
        if (i === effectivePath.length - 1) {
          targetObj = currentObj;
          break;
        }
        if (!Array.isArray(currentObj.subgroup)) currentObj.subgroup = [];
        currentArray = currentObj.subgroup;
      }
    }

    if (targetIsTopLevel) {
      newData.push(newObj);
    } else {
      if (!targetObj) return;
      if (!Array.isArray(targetObj.subgroup)) targetObj.subgroup = [];
      targetObj.subgroup.push(newObj);
    }

    const groupingWithNature = newData.map((item) =>
      applyParentNatureLocal(deepCloneLocal(item), item.nature)
    );
    const cleanedGrouping = groupingWithNature.map(cleanItemLocal);
    const payload = { grouping: cleanedGrouping };

    saveGroupingApiHandler({ groupingTemplateID: tempId }, payload)
      .then((response) => {
        if (response?.data?.success) {
          getTemplateDetails();

          setAddItemModal(false);
        }
      })
      .catch(() => {});
  };

  // ---------------- delete handler ----------------
  const deleteItemHandler = (lvl, item) => {
    const payload = {};
    const l1 = groupingData.find((g) => g._id === l1id);
    if (lvl === 1) {
      payload["category"] = item.category;
      payload["level"] = 1;
      payload["levelOne"] = item.name;
    }
    if (lvl === 2) {
      payload["category"] = selCat;
      payload["level"] = 2;
      payload["levelOne"] = l1.name;
      payload["levelTwo"] = item.name;
    }
    if (lvl === 3) {
      const l2 = l1.subgroup.find((s) => s._id === l2id);
      payload["category"] = selCat;
      payload["level"] = 3;
      payload["levelOne"] = l1.name;
      payload["levelTwo"] = l2.name;
      payload["levelThree"] = item.name;
    }
    swal({
      title: `Confirm deletion of level ${lvl} data?`,
      text: "Deleted data can be restored.",
      icon: "warning",
      buttons: {
        cancel: "Cancel",
        confirm: {
          text: "Delete",
          value: true,
          visible: true,
          className: "swal-delete-btn",
          closeModal: true,
        },
      },
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        deleteGroupingApiHandler({ groupingTemplateID: tempId }, payload)
      .then((response) => {
        if (response?.data?.success === true) getTemplateDetails();
      })
      .catch(() => {});
      }
    });
    
  };

  // ---------------- reorders ----------------
  const handleDragEnd = (result, category) => {
    if (!result.destination) return;
    setGroupingData((prevData) => {
      const items = prevData.filter((obj) => obj.category === category);
      const others = prevData.filter((obj) => obj.category !== category);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);
      const reordered = [
        ...others.filter((i) => i.category === "BS"),
        ...items.filter((i) => i.category === "BS"),
        ...others.filter((i) => i.category === "PL"),
        ...items.filter((i) => i.category === "PL"),
      ];
      prepareAndSave(reordered);
      return reordered;
    });
  };

  // Level2
  const handleLevel2DragStart = (start) => {
    const sid = start?.source?.droppableId || "";
    if (sid && sid.startsWith("lvl2-")) setDraggingLevel2(true);
  };
  const handleLevel2DragEnd = (result) => {
    if (!result.destination) {
      setDraggingLevel2(false);
      return;
    }
    if (!l1id) {
      setDraggingLevel2(false);
      return;
    }
    setGroupingData((prev) => {
      const newData = deepClone(prev || []);
      const l1Index = newData.findIndex((it) => it && it._id === l1id);
      if (l1Index === -1) {
        setDraggingLevel2(false);
        return prev;
      }
      if (!Array.isArray(newData[l1Index].subgroup))
        newData[l1Index].subgroup = [];
      const items = newData[l1Index].subgroup;
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);
      const newLvl2 = deepClone(items);
      setlvl2Data(newLvl2);

      if (l2idx !== null) {
        if (l2id) {
          const newIndex = newLvl2.findIndex((x) => x._id === l2id);
          if (newIndex !== -1) {
            setL2idx(newIndex);
          } else {
            const clamped = Math.max(0, Math.min(newLvl2.length - 1, l2idx));
            setL2idx(newLvl2.length > 0 ? clamped : null);
            setL2id(newLvl2.length > 0 ? newLvl2[clamped]._id : null);
          }
        } else {
          const clamped = Math.max(0, Math.min(newLvl2.length - 1, l2idx));
          setL2idx(newLvl2.length > 0 ? clamped : null);
          setL2id(newLvl2.length > 0 ? newLvl2[clamped]._id : null);
        }
      }

      prepareAndSave(newData);
      setDraggingLevel2(false);
      return newData;
    });
  };

  // Level3
  const handleLevel3DragStart = (start) => {
    const sid = start?.source?.droppableId || "";
    if (sid && sid.startsWith("lvl3-")) setDraggingLevel3(true);
  };
  const handleLevel3DragEnd = (result) => {
    console.log("result",result);
    
    if (!result.destination) {
      setDraggingLevel3(false);
      return;
    }
    if (!l1id || !l2id) {
      setDraggingLevel3(false);
      return;
    }

    setGroupingData((prev) => {
      const newData = deepClone(prev || []);
      const l1Index = newData.findIndex((it) => it && it._id === l1id);
      if (l1Index === -1) {
        setDraggingLevel3(false);
        return prev;
      }
      if (!Array.isArray(newData[l1Index].subgroup))
        newData[l1Index].subgroup = [];
      const lvl2Arr = newData[l1Index].subgroup || [];

      const level2Index = lvl2Arr.findIndex((x) => x._id === l2id);
      if (level2Index === -1) {
        setDraggingLevel3(false);
        return prev;
      }
      const l2Obj = lvl2Arr[level2Index];
      if (!l2Obj) {
        setDraggingLevel3(false);
        return prev;
      }
      if (!Array.isArray(l2Obj.subgroup)) l2Obj.subgroup = [];

      const items = l2Obj.subgroup;
      if (result.source.index < 0 || result.source.index >= items.length) {
        setDraggingLevel3(false);
        return prev;
      }
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);

      // optimistic: groupingData already updated; persist
      prepareAndSave(newData);
      setDraggingLevel3(false);
      return newData;
    });
  };

  // Level3 derived from groupingData via stable ids
  const currentLvl3 = useMemo(() => {
    if (!groupingData || !l1id || !l2id) return [];
    const l1 = groupingData.find((x) => x._id === l1id);
    if (!l1 || !Array.isArray(l1.subgroup)) return [];
    const l2 = l1.subgroup.find((s) => s._id === l2id);
    if (!l2 || !Array.isArray(l2.subgroup)) return [];
    return l2.subgroup;
  }, [groupingData, l1id, l2id]);

  // ---------------- build upload payload for edit ----------------
  const buildUploadPayloadForEdit = (
    level,
    itemId,
    updatedFields = {},
    context = {}
  ) => {
    if (!groupingData) return null;

    const newGrouping = deepClone(groupingData);

    const findL1IndexById = (id) =>
      newGrouping.findIndex((g) => g && g._id === id);
    const findL2IndexById = (l1Obj, id) =>
      (l1Obj?.subgroup || []).findIndex((s) => s && s._id === id);

    if (level === 1) {
      const idx = findL1IndexById(itemId);
      if (idx === -1) {
        console.warn(
          "buildUploadPayloadForEdit: level1 item not found",
          itemId,
          context
        );
        return null;
      }
      newGrouping[idx] = { ...newGrouping[idx], ...updatedFields };
    } else if (level === 2) {
      const l1idLocal =
        context.l1id ??
        (typeof context.l1idx === "number"
          ? newGrouping[context.l1idx]?._id
          : null);
      const l1Index = findL1IndexById(l1idLocal);
      if (l1Index === -1) {
        console.warn(
          "buildUploadPayloadForEdit: parent L1 not found for level2",
          l1idLocal
        );
        return null;
      }
      const l1Obj = newGrouping[l1Index];
      if (!Array.isArray(l1Obj.subgroup)) l1Obj.subgroup = [];
      const l2Index = findL2IndexById(l1Obj, itemId);
      if (l2Index === -1) {
        console.warn(
          "buildUploadPayloadForEdit: level2 item not found",
          itemId
        );
        return null;
      }
      l1Obj.subgroup[l2Index] = {
        ...l1Obj.subgroup[l2Index],
        ...updatedFields,
      };
    } else if (level === 3) {
      const l1idLocal =
        context.l1id ??
        (typeof context.l1idx === "number"
          ? newGrouping[context.l1idx]?._id
          : null);
      const l1Index = findL1IndexById(l1idLocal);
      if (l1Index === -1) {
        console.warn(
          "buildUploadPayloadForEdit: parent L1 not found for level3",
          l1idLocal
        );
        return null;
      }
      const l1Obj = newGrouping[l1Index];
      if (!Array.isArray(l1Obj.subgroup)) l1Obj.subgroup = [];

      const l2idLocal =
        context.l2id ??
        (typeof context.l2idx === "number"
          ? l1Obj.subgroup[context.l2idx]?._id
          : null);
      const l2Index = findL2IndexById(l1Obj, l2idLocal);
      if (l2Index === -1) {
        console.warn(
          "buildUploadPayloadForEdit: parent L2 not found for level3",
          l2idLocal
        );
        return null;
      }
      const l2Obj = l1Obj.subgroup[l2Index];
      if (!Array.isArray(l2Obj.subgroup)) l2Obj.subgroup = [];

      const l3Index = l2Obj.subgroup.findIndex((s) => s && s._id === itemId);
      if (l3Index === -1) {
        console.warn(
          "buildUploadPayloadForEdit: level3 item not found",
          itemId
        );
        return null;
      }
      l2Obj.subgroup[l3Index] = {
        ...l2Obj.subgroup[l3Index],
        ...updatedFields,
      };
    } else {
      console.warn("buildUploadPayloadForEdit: unsupported level", level);
      return null;
    }

    // apply nature and cleaning
    const groupingWithNature = newGrouping.map((it) =>
      applyParentNature(deepClone(it), it.nature)
    );
    const cleanedGrouping = groupingWithNature.map(cleanItem);
    const payload = { grouping: cleanedGrouping };

    console.groupCollapsed("buildUploadPayloadForEdit()");
    console.log("level:", level, "itemId:", itemId, "context:", context);
    console.log("updatedFields:", updatedFields);
    console.log("payload snapshot:", JSON.parse(JSON.stringify(payload)));
    console.groupEnd();

    return payload;
  };

  // ---------------- handle saving from modal ----------------
  const handleSaveFromModal = (level, item, updatedFields, context = {}) => {
    // Build payload
    const payload = buildUploadPayloadForEdit(
      level,
      item._id,
      updatedFields,
      context
    );
    if (!payload) {
      console.error("Could not build payload for edit");
      return;
    }

    // Send to backend
    saveGroupingApiHandler({ groupingTemplateID: tempId }, payload)
      .then((res) => {
        if (res?.data?.success) {
          // refresh authoritative data
          getTemplateDetails();
          setShowEditItemModal(false);
        } else {
          console.error("Save failed", res?.data);
        }
      })
      .catch((err) => {
        console.error("save error", err);
      });
  };

  // ---------------- logging helper (optional) ----------------
  const cleanForLogging = (item) => {
    if (!item) return item;
    if (item.lineType === "line item") {
      const { formula, ...rest } = item;
      if (Array.isArray(rest.subgroup) && rest.subgroup.length > 0)
        rest.subgroup = rest.subgroup.map(cleanForLogging);
      return rest;
    } else if (item.lineType === "formula") {
      const { nature, subgroup, ...rest } = item;
      if (Array.isArray(item.formula) && item.formula.length > 0)
        rest.formula = item.formula.map((f) => ({ ...f }));
      return rest;
    }
    return item;
  };

  // ---------------- render helpers ----------------
  const renderCategoryBlock = (category, label) => (
    <div className={libcss.levelCat}>
      <span className={libcss.levelCatLabel}>{label}</span>
      <div className={libcss.levelCatDiv}>
        <DragDropContext
          onDragEnd={(result) => handleDragEnd(result, category)}
        >
          <Droppable droppableId={category}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {groupingData &&
                  groupingData
                    .filter((obj) => obj.category === category)
                    .map((obj, idx) => (
                      <Draggable
                        key={obj._id || `${category}-${idx}`}
                        draggableId={obj._id || `${category}-${idx}`}
                        index={idx}
                        isDragDisabled={isReadOnly}
                      >
                        {(prov) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            style={{ ...(prov.draggableProps.style || {}) }}
                            className={`${libcss.levelItem} ${
                              commoncss.clickable
                            } ${isReadOnly ? libcss.dragDisabled : ""} ${
                              obj._id === l1id ? libcss.levelItemSelected : ""
                            }`}
                            onClick={() => {
                              setL1idx(idx);
                              setL1id(obj._id);
                              setAddItemCat(category);
                              const firstSub = obj?.subgroup?.[0];
                              setL2idx(firstSub ? 0 : null);
                              setL2id(firstSub ? firstSub._id : null);
                              setSelCat(category);
                            }}
                          >
                            <span>
                              <span className={libcss.menuIconWrapper}>
                                <img src={MenuIcon} alt="menu" />
                              </span>

                              {obj.lineType === "formula" && (
                                <FormulaIcon width={16} height={16} />
                              )}
                              &ensp;{obj.name}
                            </span>
                            {!isReadOnly && (
                              <div className={libcss.lineActions}>
                                <EditIcon
                                  width={15}
                                  height={15}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // open modal with level1 item
                                    setEditItemLevel(1);
                                    setEditItem(obj);
                                    setEditContext({
                                      l1idx: idx,
                                      l1id: obj._id,
                                      category,
                                    });
                                    setShowEditItemModal(true);
                                  }}
                                />
                                &ensp;
                                <CloseIcon
                                  width={16}
                                  height={16}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteItemHandler(1, obj);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {!isReadOnly && (
          <div
            className={` ${libcss.addLabel} ${commoncss.clickable}`}
            onClick={() => {
              setAddItemModal(true);
              setAddItemLevel(1);
              setAddItemCat(category);
            }}
          >
            <Plus height={15} width={15} /> Add Level 1
          </div>
        )}
      </div>
    </div>
  );

 //EmptyState when no data is selected from Level 1 and Level 2 
const EmptyLevelState = ({ levelText, title, subtitle }) => {
 return (
    <div className={libcss.emptyStateWrapper}>
      <div className={libcss.emptyBadge}>
        <span className={libcss.emptyBadgeText}>{levelText}</span>
        <div className={libcss.emptyBadgeIcon}>
          <img src={closeSideModel} alt="Close" style={{ width: "40px", height: "40px", objectFit: "contain"}}/>
        </div>
      </div>

      <div className={libcss.emptyTitle}>{title}</div>
      <div className={libcss.emptySubtitle}>{subtitle}</div>
    </div>
  );
};

  return (
    <>
      <motion.div
        initial={{ x: 500, y: 500, opacity: 0 }}
        animate={{ x: 0, y: 0, opacity: 1 }}
        exit={{ x: 0, y: 0, opacity: 0 }}
        transition={{ duration: 0.7, ease: "easeInOut" }}
        className={libcss.hierarchyLayout}
        style={{ border: "0px", marginTop: "1%" }}
      >
        <div className={libcss.levelDivs}>
          <span className={libcss.levelLabel}>Level 1</span>
          <div
            className={`${libcss.levelCatDiv} ${libcss.levelCatMaxheight}  ${commoncss.hideScroll}`}
          >
            {renderCategoryBlock("BS", "Balancesheet")}
            {renderCategoryBlock("PL", "P&L")}
          </div>
        </div>

        <div className={libcss.levelDivs}>
          <span className={libcss.levelLabel}>
            LEVEL 1
              <img src={Arrow} className={libcss.levelArrow} alt="arrow" />
           <span className={!l1id ? libcss.levelDimmed : "levelLabel"}>
            LEVEL 2
           </span>
          </span>
          <div
            className={`${libcss.levelCatDiv} ${libcss.levelCatMaxheight}  ${commoncss.hideScroll}`}
          >
             {!l1id ? (
             <EmptyLevelState
              levelText="LV 1"
              title="No Level 1 selected"
              subtitle="Select a level 1 item to view the level 2 items associated with it"
            />
          ) : (
            <DragDropContext
              onDragStart={handleLevel2DragStart}
              onDragEnd={handleLevel2DragEnd}
            >
              <Droppable droppableId={`lvl2-${l1id || "none"}`}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {lvl2Data &&
                      lvl2Data.map((obj, idx) => (
                        <Draggable
                          key={obj._id || `lvl2-${idx}`}
                          draggableId={obj._id || `lvl2-${idx}`}
                          index={idx}
                          isDragDisabled={isReadOnly}
                        >
                          {(prov, snapshot) => {
                            const style = prov.draggableProps.style || {};
                            const draggingClass = snapshot.isDragging
                              ? libcss.draggingItem
                              : "";
                            return (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                                style={{ ...style }}
                                className={`${libcss.levelItem} ${
                                  commoncss.clickable
                                } ${isReadOnly ? libcss.dragDisabled : ""}  ${
                                  idx === l2idx ? libcss.levelItemSelected : ""
                                } ${draggingClass}`}
                                onClick={() => {
                                  setL2idx(idx);
                                  setL2id(obj._id ?? null);
                                }}
                              >
                                <span>
                                <span className={libcss.menuIconWrapper}>
                                  <img src={MenuIcon} alt="menu" />
                                </span>
                                  {obj.lineType === "formula" && (
                                    <FormulaIcon width={16} height={16} />
                                  )}
                                  &ensp;{obj.name}
                                </span>
                                {!isReadOnly && (
                                  <div className={libcss.lineActions}>
                                    <EditIcon
                                      width={15}
                                      height={15}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditItemLevel(2);
                                        setEditItem(obj);
                                        setEditContext({
                                          l1id,
                                          l1idx,
                                          l2idx: idx,
                                          l2id: obj._id,
                                        });
                                        setShowEditItemModal(true);
                                      }}
                                    />
                                    &ensp;
                                    <CloseIcon
                                      width={16}
                                      height={16}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteItemHandler(2, obj);
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          }}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
            {!isReadOnly && l1id && (
              <div
                className={` ${libcss.addLabel} ${commoncss.clickable}`}
                onClick={() => {
                  setAddItemModal(true);
                  setAddItemLevel(2);
                }}
              >
                <Plus height={15} width={15} /> Add Level 2
              </div>
            )}
          </div>
        </div>

        {/* LEVEL 3 */}
        <div className={libcss.levelDivs}>
          <span className={libcss.levelLabel}>
            Level 1    
            <img src={Arrow} className={libcss.levelArrow} alt="arrow" />
              <span className={!l1id ? libcss.levelDimmed : libcss.levelActive}>
                LEVEL 2
              </span>
            <img src={Arrow} className={libcss.levelArrow} alt="arrow" />
            <span className={!l1id ? libcss.levelDimmed : libcss.levelActive}>
                LEVEL 3
            </span>
          </span>

        
          <div
            className={`${libcss.levelCatDiv} ${libcss.levelCatMaxheight} ${commoncss.hideScroll}`}
          >
            {!l2id ? (
            <EmptyLevelState
             levelText="LV 2"
             title="No Level 2 selected"
             subtitle="Select a level 2 item to view the level 3 items associated with it"
            />
          ) : (
            <DragDropContext
              onDragStart={handleLevel3DragStart}
              onDragEnd={handleLevel3DragEnd}
            >
              <Droppable
                droppableId={`lvl3-${l1id || "none"}-${l2id || "none"}`}
              >
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {currentLvl3 &&
                      currentLvl3.map((obj, idx) => (
                        <Draggable
                          key={obj._id || `${l2id}-lvl3-${idx}`}
                          draggableId={obj._id || `${l2id}-lvl3-${idx}`}
                          index={idx}
                          isDragDisabled={isReadOnly}
                        >
                          {(prov, snapshot) => {
                            const style = prov.draggableProps.style || {};
                            const draggingClass = snapshot.isDragging
                              ? libcss.draggingItem
                              : "";
                            return (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                                style={{ ...style }}
                                className={`${
                                  libcss.levelItem
                                } ${draggingClass} ${
                                  isReadOnly ? libcss.dragDisabled : ""
                                } `}
                                onClick={() => setL3idx(idx)}
                              >
                                <span>
                                <span className={libcss.menuIconWrapper}>
                                  <img src={MenuIcon} alt="menu" />
                                </span>
                                  {obj.lineType === "formula" && (
                                    <FormulaIcon width={16} height={16} />
                                  )}
                                  &ensp;{obj.name}
                                </span>
                                {!isReadOnly && (
                                  <div className={libcss.lineActions}>
                                    <EditIcon
                                      width={15}
                                      height={15}
                                      className={commoncss.clickable}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditItemLevel(3);
                                        setEditItem(obj);
                                        setEditContext({
                                          l1id,
                                          l1idx,
                                          l2id,
                                          l2idx,
                                          l3idx: idx,
                                        });
                                        setShowEditItemModal(true);
                                      }}
                                    />
                                    &ensp;
                                    <CloseIcon
                                      width={16}
                                      height={16}
                                      className={commoncss.clickable}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteItemHandler(3, obj);
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          }}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
            {!isReadOnly && l2id && (
              <div
                className={` ${libcss.addLabel} ${commoncss.clickable}`}
                onClick={() => {
                  setAddItemModal(true);
                  setAddItemLevel(3);
                }}
              >
                <Plus height={15} width={15} /> Add Level 3
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* MODALS */}
      {addItemModal && !isReadOnly && (
        <AddItemModal
          groupingData={groupingData}
          addItemLevel={addItemLevel}
          addItemCat={addItemCat}
          addItemHandler={AddItemHandler}
          addToSubgroup={addToSubgroup}
          setAddItemModal={setAddItemModal}
          l1id={l1id}
          l1idx={l1idx}
          l2idx={l2idx}
          l3idx={l3idx}
        />
      )}

      {showEditItemModal && editItem && !isReadOnly && (
        <ShowEditItemModal
          groupingData={groupingData}
          item={editItem}
          level={editItemLevel}
          context={editContext}
          // ShowEditItemModal must call this when user clicks Save with updated fields:
          onSave={(updatedFields) => {
            handleSaveFromModal(
              editItemLevel,
              editItem,
              updatedFields,
              editContext
            );
          }}
          onClose={() => {
            setShowEditItemModal(false);
            setEditItem(null);
            setEditContext(null);
            setEditItemLevel(null);
          }}
        />
      )}

      {showBin === "hierarchy" && !isReadOnly && (
        <RestoreDeletedItemModal
          tempId={tempId}
          deletedGroupingData={deletedGroupingData}
          setShowBin={setShowBin}
        />
      )}
    </>
  );
};

export default Hierarchy;
